import type {
  DtoApplication,
  DtoApplicationBorrower,
} from "@porchpass/api-client";
import {
  getAuthBorrowerInfo,
  getIdsFromPath,
  getMainBorrowerIdFromApplication,
  getBorrowerFromApplicationById,
  getCoBorrowerIdsFromApplication,
} from "src/utils";
import type { DashboardRedirectHandlerParams } from "src/types/dashboard";
import type { SetUserDataProps } from "../../types";

export const setRedirectedUserData = (
  application: DtoApplication,
  authBorrower: DtoApplicationBorrower,
  mainBorrower: string | undefined,
  redirectBorrower: DtoApplicationBorrower,
  setUserData?: SetUserDataProps,
) => {
  const { is_primary, status, id: redirectBorrowerId } = redirectBorrower;
  const coborrowerIds = getCoBorrowerIdsFromApplication(application);

  setUserData?.((prev) => ({
    ...prev,
    status,
    is_primary,
    application_id: application.id,
    auth_user_borrower_id: authBorrower.id,
    dealer_id: application.dealer?.id,
    borrower_id: redirectBorrowerId,
    roles: {
      borrower: mainBorrower,
      coborrowers: coborrowerIds,
    },
  }));
};

export const handleRedirectParam = (
  applications: DtoApplication[],
  {
    dealerName,
    setUserData,
    navigate,
    firebaseUser,
  }: DashboardRedirectHandlerParams,
) => {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectParam = urlParams.get("redirect");
  if (
    !redirectParam ||
    !redirectParam.startsWith(`/${dealerName}/application/`) ||
    redirectParam.includes(".")
  ) {
    return false;
  }

  const pathIds = getIdsFromPath(redirectParam);
  if (
    !pathIds.application_id ||
    !pathIds.borrower_id ||
    !pathIds.dealer_id ||
    pathIds.dealer_id !== dealerName
  ) {
    return false;
  }

  for (const application of applications) {
    const authBorrower = getAuthBorrowerInfo(application, firebaseUser);
    const mainBorrower = getMainBorrowerIdFromApplication(application);
    const redirectBorrower = getBorrowerFromApplicationById(
      application,
      pathIds.borrower_id,
    );
    const { id: redirectBorrowerId } = redirectBorrower;

    if (
      !redirectBorrowerId ||
      (pathIds.borrower_id !== authBorrower.id &&
        pathIds.borrower_id !== mainBorrower)
    ) {
      continue;
    }

    if (application.id === pathIds.application_id) {
      setRedirectedUserData(
        application,
        authBorrower,
        mainBorrower,
        redirectBorrower,
        setUserData,
      );
      /**
       * WARNING:
       *  This is a potential security risk if @redirectParam is not validated properly.
       *  NEVER directly pass URL params without validating them first.
       *
       *  This particular instance is "safe" because:
       *    - The above validations
       *    - navigate() function can only handle internal routing within the react app via a path string
       */
      console.log(`Redirecting to ${redirectParam}`);
      navigate(redirectParam);
      return true;
    }
  }

  return false;
};
